import { Button, MenuItem, Select, styled } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { Link } from "react-router-dom";
import StatusChip from "../../../common/js/components/StatusChip";
import { ListingCircle } from "../../../common/js/icons";
import utils, { aggregateBrokers, buildAddress, toDollarAmount, toSqft } from "../utils/operations";
import "./Listings.css";
import { buttonActions } from "./reviewListingsTables";

const StyledListingIdLink = styled("span")(() => ({
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
    color: "#2C99D2",
    fontWeight: 600,
}));

const StyledListingIdLinkUnassigned = styled("span")({
    color: "black",
    fontWeight: 600,
});

const StyledLinkButton = styled(Link)(() => ({
    textDecoration: "none",
}));

const StyledListingNumber = styled("h4")(() => ({
    fontSize: "14px",
    margin: "0",
    borderColor: "#f1f1f1",
    color: "#2C99D2",
    fontWeight: 600,
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
}));

const StyledSelectTitle = styled(Select)({
    fontWeight: "600",
    color: "rgb(158, 158, 158)",
    fontSize: "12px",
    height: "20px",
    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
    "& .MuiSelect-select": { padding: 0 },
    paddingLeft: "2px",
});

const StyledSelectSpan = styled("span")({
    color: "rgb(158, 158, 158)",
    fontSize: "12px",
    fontWeight: "600",
});

const StyledPropertyContainerDiv = styled("div")({
    display: "flex",
    alignItems: "center",
    gap: "1vw",
});

const StyledListingInfoDiv = styled("div")({
    display: "flex",
    flexDirection: "column",
});

const renderBrokers = (brokers) => {
    if (!brokers) return "";
    const brokerNames = brokers.split(",");
    if (brokerNames.length > 1) {
        return (
            <Tooltip title={brokers} placement="top">
                <span>{`${brokerNames.length} Brokers`}</span>
            </Tooltip>
        );
    }
    return brokers;
};

export const filterColumns = {
    broker_requested: [
        {
            field: "address",
            label: "Address Line",
            type: "plaintext",
            transform: (property) => {
                return (property?.primaryAddress?.address1 ?? "") + " " + (property?.primaryAddress?.address2 ?? "");
            },
        },
        {
            field: "city",
            label: "City",
            type: "plaintext",
        },
        {
            field: "stateCode",
            label: "State",
            type: "plaintext",
        },
        {
            field: "zipCode",
            label: "Zipcode",
            type: "plaintext",
        },
        {
            field: "dateAvailable",
            label: "Date Avail",
            type: "date",
            transform: utils.formatDate("MM-DD-YYYY"),
        },
        {
            field: "listingType",
            label: "Type",
            type: "plaintext",
        },
        {
            field: "listingName",
            label: "Listing Name",
            type: "plaintext",
        },
        {
            field: "rate",
            label: "Rate",
            type: "currency",
            numeric: true,
        },
        {
            field: "sfAvail",
            label: "SQFT",
            type: "numberRange",
        },
        {
            field: "status",
            label: "Status",
            type: "plaintext",
        },
    ],
    data_team_unassigned: [
        {
            field: "address",
            label: "Address Line",
            type: "plaintext",
        },
        {
            field: "city",
            label: "City",
            type: "plaintext",
        },
        {
            field: "stateCode",
            label: "State",
            type: "plaintext",
        },
        {
            field: "zipCode",
            label: "Zipcode",
            type: "plaintext",
        },
        {
            field: "brokers",
            label: "Brokers",
            type: "plaintext",
        },
        {
            field: "auditLastModifiedTimestamp",
            label: "Modified",
            type: "date",
        },
        {
            field: "listingType",
            label: "Type",
            type: "plaintext",
        },
        {
            field: "rate",
            label: "Rate",
            type: "currency",
        },
        {
            field: "sfAvail",
            label: "SQFT",
            type: "numberRange",
        },
        {
            field: "status",
            label: "Status",
            type: "plaintext",
        },
        {
            field: "listingName",
            label: "Listing Name",
            type: "plaintext",
        },
    ],
};

export const dataGridColumns = (sortListingOrAddress, setSortListingOrAddress, isGrouped) => ({
    broker_requested: [
        {
            headerName: "Address",
            field: "property",
            flex: 1.8,
            minWidth: 200,
            renderHeader: (params) => {
                return (
                    <div>
                        <span className="MuiDataGrid-columnHeaderTitle">Address</span>
                        {!isGrouped && (
                            <div>
                                <StyledSelectSpan variant="standard">Sort By: </StyledSelectSpan>
                                <StyledSelectTitle
                                    value={sortListingOrAddress}
                                    onChange={(e) => setSortListingOrAddress(e.target.value)}
                                    disableUnderline
                                >
                                    <MenuItem value={"property.primaryAddress"}>Address</MenuItem>
                                    <MenuItem value={"listingName"}>Listing Name</MenuItem>
                                </StyledSelectTitle>
                            </div>
                        )}
                    </div>
                );
            },
            renderCell: (params) => {
                const listing = params?.row?.listing;
                const property = params?.row?.property;
                const listingsLength = params?.row?.listingsLength;
                return params?.row?.isGrouped ? (
                    <StyledPropertyContainerDiv>
                        <div>
                            <ListingCircle />
                        </div>
                        <div>
                            <div className="cluster">
                                <span className="clusterHeader">
                                    {(property?.propertyAttributes?.propertyName ?? null)
                                        ? (property?.propertyAttributes?.propertyName ?? "")
                                        : (property?.primaryAddress?.address1 ?? "")}
                                </span>
                                <span className="clusterSubHeader">
                                    {`${listingsLength} ${listingsLength === 1 ? "listing" : "listings"}`}
                                </span>
                            </div>
                        </div>
                    </StyledPropertyContainerDiv>
                ) : (
                    <StyledListingInfoDiv>
                        <StyledLinkButton to={`/listing/${listing?.listingKey}`}>
                            <StyledListingNumber>{`No. ${listing?.listingKey ?? ""}`}</StyledListingNumber>
                            <StyledListingIdLink>{listing?.listingName ?? ""}</StyledListingIdLink>
                        </StyledLinkButton>
                        <div>
                            <span className="clusterSubHeader">
                                {buildAddress(listing?.property?.primaryAddress ?? {})}
                            </span>
                        </div>
                    </StyledListingInfoDiv>
                );
            },
            valueGetter: (value, row) => {
                return (value?.primaryAddress?.address1 ?? "") + " " + (value?.primaryAddress?.address2 ?? "");
            },
        },
        {
            headerName: "Date Avail",
            field: "dateAvailable",
            flex: 0.5,
            minWidth: 100,
            valueGetter: (value, row) => utils.formatDate("MM-DD-YYYY")(value),
        },
        {
            headerName: "Type",
            field: "listingType",
            flex: 0.3,
            minWidth: 80,
        },
        {
            headerName: "Rate",
            field: "rate",
            flex: 0.6,
            minWidth: 80,
            numeric: true,
            valueGetter: (value, row) => {
                const listing = row?.listing;
                return listing?.listingType === "Lease"
                    ? `${toDollarAmount(listing?.askingRentMax)}`
                    : `${toDollarAmount(listing?.listPrice ?? "-")}`;
            },
        },
        {
            headerName: "SQFT",
            field: "sfAvail",
            flex: 0.6,
            minWidth: 80,
            valueGetter: (value, row) => {
                const listing = row?.listing;
                return row?.isGrouped
                    ? null
                    : listing?.listingType === "Lease"
                      ? listing?.sfDivisibleTo
                          ? `${toSqft(listing?.sfDivisibleTo)} - ${toSqft(listing?.sfAvail)}`
                          : `${toSqft(listing?.sfAvail ?? "-")}`
                      : `${toSqft(listing?.sfAvail ?? "-")}`;
            },
        },
        {
            headerName: "Status",
            field: "status",
            flex: 0.8,
            minWidth: 200,
            renderCell: (params) => {
                const listing = params?.row?.listing;
                const role = params?.row?.role;
                const buttonProperties = buttonActions?.[role]?.[listing?.status] ?? buttonActions.defaultAction;
                return (
                    !params?.row?.isGrouped && (
                        <StatusChip
                            status={
                                buttonProperties.statusToDisplay && buttonProperties.statusToDisplay !== ""
                                    ? buttonProperties.statusToDisplay
                                    : listing?.status
                            }
                        />
                    )
                );
            },
        },
        {
            headerName: "",
            field: "navButton",
            sortable: false,
            renderCell: (params) => {
                const listing = params?.row?.listing;
                const role = params?.row?.role;
                const buttonProperties = buttonActions?.[role]?.[listing?.status] ?? buttonActions.defaultAction;
                return (
                    !params?.row?.isGrouped && (
                        <StyledLinkButton to={`/listing/${listing?.listingKey}`}>
                            <Button variant={buttonProperties.variant} color="primary" onClick={() => null}>
                                {buttonProperties.text}
                            </Button>
                        </StyledLinkButton>
                    )
                );
            },
        },
    ],
    data_team_unassigned: [
        {
            headerName: "Address",
            field: "property",
            flex: 2,
            minWidth: 430,
            renderHeader: (params) => {
                return (
                    <div>
                        <span className="MuiDataGrid-columnHeaderTitle">Address</span>
                        {!isGrouped && (
                            <div>
                                <StyledSelectSpan variant="standard">Sort By: </StyledSelectSpan>
                                <StyledSelectTitle
                                    value={sortListingOrAddress}
                                    onChange={(e) => setSortListingOrAddress(e.target.value)}
                                    disableUnderline
                                >
                                    <MenuItem value={"property.primaryAddress"}>Address</MenuItem>
                                    <MenuItem value={"listingName"}>Listing Name</MenuItem>
                                </StyledSelectTitle>
                            </div>
                        )}
                    </div>
                );
            },
            renderCell: (params) => {
                const listing = params?.row?.listing;
                const property = params?.row?.property;
                const listingsLength = params?.row?.listingsLength;
                return params?.row?.isGrouped ? (
                    <StyledPropertyContainerDiv>
                        <div>
                            <ListingCircle />
                        </div>
                        <div>
                            <div className="cluster">
                                <span className="clusterHeader">
                                    {(property?.propertyAttributes?.propertyName ?? null)
                                        ? (property?.propertyAttributes?.propertyName ?? "")
                                        : (property?.primaryAddress?.address1 ?? "")}
                                </span>
                                <span className="clusterSubHeader">
                                    {`${listingsLength} ${listingsLength === 1 ? "listing" : "listings"}`}
                                </span>
                            </div>
                        </div>
                    </StyledPropertyContainerDiv>
                ) : (
                    <StyledListingInfoDiv>
                        <StyledLinkButton to={`/listing/${listing?.listingKey}`}>
                            <StyledListingNumber>{`No. ${listing?.listingKey ?? ""}`}</StyledListingNumber>
                            <StyledListingIdLinkUnassigned>{listing?.listingName ?? ""}</StyledListingIdLinkUnassigned>
                        </StyledLinkButton>
                        <div>
                            <span className="clusterSubHeader">
                                {buildAddress(listing?.property?.primaryAddress ?? {})}
                            </span>
                        </div>
                    </StyledListingInfoDiv>
                );
            },
        },
        {
            headerName: "Brokers",
            field: "brokers",
            flex: 0.5,
            minWidth: 90,
            renderCell: (params) => {
                if (params?.row?.isGrouped) return;

                const listing = params?.row?.listing;

                return renderBrokers(listing?.brokers);
            },
            valueGetter: (value, row) => {
                return aggregateBrokers(value, "brokerAgentKey.person");
            },
        },
        {
            headerName: "Modified",
            field: "auditLastModifiedTimestamp",
            flex: 0.5,
            minWidth: 100,
            valueGetter: (value, row) => {
                return utils.formatDate("MM-DD-YYYY")(value);
            },
        },
        {
            headerName: "Type",
            field: "listingType",
            flex: 0.5,
            minWidth: 80,
        },
        {
            headerName: "Rate",
            field: "rate",
            minWidth: 80,
            numeric: true,
            valueGetter: (value, row) => {
                const listing = row?.listing;
                return listing?.listingType === "Lease"
                    ? `${toDollarAmount(listing?.askingRentMax)}`
                    : `${toDollarAmount(listing?.listPrice ?? "-")}`;
            },
        },
        {
            headerName: "SQFT",
            field: "sfAvail",
            minWidth: 80,
            valueGetter: (value, row) => {
                const listing = row?.listing;
                return row?.isGrouped
                    ? null
                    : listing?.listingType === "Lease"
                      ? listing?.sfDivisibleTo
                          ? `${toSqft(listing?.sfDivisibleTo)} - ${toSqft(listing?.sfAvail)}`
                          : `${toSqft(listing?.sfAvail ?? "-")}`
                      : `${toSqft(listing?.sfAvail ?? "-")}`;
            },
        },
        {
            headerName: "Status",
            field: "status",
            minWidth: 150,
            renderCell: (params) => {
                const listing = params?.row?.listing;
                const role = params?.row?.role;
                const buttonProperties = buttonActions?.[role]?.[listing?.status] ?? buttonActions.defaultAction;
                return (
                    !params?.row?.isGrouped && (
                        <StatusChip
                            status={
                                buttonProperties.statusToDisplay && buttonProperties.statusToDisplay !== ""
                                    ? buttonProperties.statusToDisplay
                                    : listing?.status
                            }
                        />
                    )
                );
            },
        },
        {
            headerName: "",
            field: "navButton",
            sortable: false,
            renderCell: (params) => {
                const listing = params?.row?.listing;
                const role = params?.row?.role;
                const buttonProperties = buttonActions?.[role]?.[listing?.status] ?? buttonActions.defaultAction;
                return (
                    !params?.row?.isGrouped && (
                        <StyledLinkButton to={`/listing/${listing?.listingKey}`}>
                            <Button variant={buttonProperties.variant} color="primary" onClick={() => null}>
                                {buttonProperties.text}
                            </Button>
                        </StyledLinkButton>
                    )
                );
            },
        },
    ],
});
